import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import DateTimePicker from '../DateTimePicker';
import { ProfileField, MomentType } from '../../../types/profile';
import useDelayedEffect from '../../../hooks/useDelayedEffect';

interface MomentInputProps {
  field: ProfileField;
  setField: (field: ProfileField) => void;
  canBeEmpty?: boolean;
  mandatory?: boolean;
  validate?: boolean;
}

export default function MomentInput({
  field,
  setField,
  canBeEmpty = false,
  mandatory = false,
  validate = false,
}: MomentInputProps): JSX.Element {
  const { t } = useTranslation();
  const [error, setError] = useState<string>('');

  const momentName = field.moment?.name || '';
  const momentType = field.moment?.momentType || MomentType.WEDDING;
  const momentDate = field.moment?.date || '';

  useDelayedEffect(() => {
    if (!validate) return;
    if (!momentName || !momentDate) {
      setError(canBeEmpty ? '' : t('invalid.empty'));
      return;
    }
    // Validate date format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(momentDate)) {
      setError(t('invalid.invalid'));
      return;
    }
    setError('');
  }, [momentName, momentDate, validate]);

  const handleNameChange = (name: string) => {
    setField({
      ...field,
      moment: {
        name,
        momentType: field.moment?.momentType || MomentType.WEDDING,
        date: field.moment?.date || '',
      },
    });
  };

  const handleTypeChange = (type: MomentType) => {
    setField({
      ...field,
      moment: {
        name: field.moment?.name || '',
        momentType: type,
        date: field.moment?.date || '',
      },
    });
  };

  const handleDateChange = (date: string) => {
    setField({
      ...field,
      moment: {
        name: field.moment?.name || '',
        momentType: field.moment?.momentType || MomentType.WEDDING,
        date,
      },
    });
  };

  return (
    <div className="relative flex max-w-2xl flex-col gap-3">
      <div className="flex flex-col">
        <label className="mb-1 font-medium">
          {t('page.profile.specialDates')} {mandatory ? '*' : ''}
        </label>
        <input
          type="text"
          className={classNames(
            'h-10 w-full rounded-lg border !bg-secondary-50 px-2 text-gray-600 focus:ring-0 focus:ring-transparent',
            {
              '!border-error': error,
            },
          )}
          placeholder={t('page.profile.momentName')}
          value={momentName}
          onChange={(e) => handleNameChange(e.target.value)}
        />
      </div>

      <div className="flex gap-2">
        <div className="flex-1">
          <label className="mb-1 block text-sm font-medium">{t('page.profile.momentType')}</label>
          <select
            className="h-10 w-full rounded-lg border px-2 text-gray-600 focus:ring-0 focus:ring-transparent"
            value={momentType}
            onChange={(e) => handleTypeChange(e.target.value as MomentType)}>
            <option value={MomentType.WEDDING}>{t('page.profile.momentTypes.wedding')}</option>
            <option value={MomentType.CHILDBIRTH}>{t('page.profile.momentTypes.childbirth')}</option>
          </select>
        </div>

        <div className="flex-1">
          <label className="mb-1 block text-sm font-medium">{t('page.profile.momentDate')}</label>
          <DateTimePicker
            value={momentDate ? new Date(momentDate) : undefined}
            onChange={(date) => handleDateChange(date ? date.toISOString().slice(0, 10) : '')}
            dateOnly
            className={classNames(
              'h-10 w-full rounded-lg border !bg-secondary-50 px-2 text-gray-600 focus:ring-0 focus:ring-transparent',
              {
                '!border-error': error,
              },
            )}
            ariaLabel={t('page.profile.momentDate')}
          />
        </div>
      </div>

      {error && <p className="text-sm text-error">{error}</p>}
    </div>
  );
}
